import { type FC, type ReactNode, useState } from "react";

import { useQuery } from "@apollo/client/react";

import { faFileLines } from "@fortawesome/pro-duotone-svg-icons/faFileLines";
import { faMicrophone } from "@fortawesome/pro-duotone-svg-icons/faMicrophone";

import {
  DocumentKindEnum,
  ProjectTranscriptDocument,
  type TranscriptDocumentFragment,
  TranscriptStateEnum,
} from "@app_schema";

import { formatClock } from "@application/utilities/format_clock";

import { AudioPlayer } from "@styled/audio_player";
import { Segmented } from "@styled/segmented";
import {
  TranscriptBody,
  TranscriptHead,
  TranscriptNote,
  TranscriptToolbar,
} from "@styled/transcript_sheet";

import { TranscriptActions } from "./transcript_actions";
import { transcriptKindLabel } from "./transcript_files";

type View = "transcript" | "original";

// A transcript not ready to read, as the Dictation tab and the file's own page
// say it.
export const TranscriptStateNote: FC<{ state: TranscriptStateEnum }> = ({
  state,
}) => (
  <TranscriptNote>
    {state === TranscriptStateEnum.Failed
      ? "We couldn't transcribe this file. Upload it again to retry."
      : "Transcribing this file…"}
  </TranscriptNote>
);

const VIEWS = [
  { value: "transcript", label: "Transcript" },
  { value: "original", label: "Original" },
];

// One dictation file: the cleaned Transcript or the verbatim Original, with
// the audio to play back beside it. The text loads only for the file on
// screen, so the case page's polling never carries it.
export const TranscriptReader: FC<{
  projectId: string;
  document: TranscriptDocumentFragment;
}> = ({ projectId, document }) => {
  const [view, setView] = useState<View>("transcript");
  const state = document.transcriptState;
  const { data } = useQuery(ProjectTranscriptDocument, {
    variables: { projectID: projectId, documentID: document.id },
    skip: state !== TranscriptStateEnum.Ready,
  });
  const transcript = data?.project.document.transcript;
  const text =
    (view === "transcript" ? transcript?.cleanedText : transcript?.rawText) ??
    "";

  const isAudio = document.kind === DocumentKindEnum.AudioRecording;
  const seconds = document.transcript?.durationSeconds;
  const meta = [seconds && formatClock(seconds), `from ${document.name}`]
    .filter(Boolean)
    .join(" · ");

  let body: ReactNode;
  if (state && state !== TranscriptStateEnum.Ready) {
    body = <TranscriptStateNote state={state} />;
  } else if (!transcript) {
    body = <TranscriptNote>Loading the transcript…</TranscriptNote>;
  } else if (view === "transcript") {
    body = <TranscriptBody html={transcript.cleanedHtml ?? ""} />;
  } else {
    body = <TranscriptBody text={transcript.rawText ?? ""} />;
  }

  return (
    <>
      <TranscriptToolbar
        leading={
          <Segmented
            items={VIEWS}
            selectedValue={view}
            onChange={(value) => setView(value as View)}
          />
        }
        trailing={
          <TranscriptActions
            projectId={projectId}
            document={document}
            text={text}
          />
        }
      />
      <TranscriptHead
        icon={isAudio ? faMicrophone : faFileLines}
        title={transcriptKindLabel(document.kind)}
        meta={meta}
      >
        {isAudio && (
          <AudioPlayer
            key={document.id}
            src={document.file.url}
            duration={seconds}
          />
        )}
      </TranscriptHead>
      {body}
    </>
  );
};
